export type SubAgentId =
  | "policy-review"
  | "claim-photo-analysis"
  | "mediation-expert"
  | "fraud-risk"
  | "coverage-gap"
  | "intake-conversion";

export interface SubAgentConfig {
  id: SubAgentId;
  name: string;
  displayName: string;
  description: string;
  modelId: string;
  specialty: string;
  enabled: boolean;
}

const SUB_AGENT_TOGGLES: Record<SubAgentId, boolean> = {
  "policy-review": true,
  "claim-photo-analysis": false,
  "mediation-expert": true,
  "fraud-risk": false,
  "coverage-gap": true,
  "intake-conversion": true
};

const BASE_SUB_AGENT_CONFIGS: Omit<SubAgentConfig, "enabled">[] = [
  {
    id: "policy-review",
    name: "Coverage Review Agent",
    displayName: "Coverage Review Specialist",
    description:
      "Analyzes policy language, endorsements, exclusions, limits, and coverage posture for claim strategy.",
    modelId: "gpt-5.4",
    specialty: "Coverage interpretation"
  },
  {
    id: "claim-photo-analysis",
    name: "Claim Photo Analysis Agent",
    displayName: "Claim Photo Analysis Sub Agent",
    description:
      "Reviews photo evidence and produces damage observations with documentation findings.",
    modelId: "gpt-5.4-mini",
    specialty: "Visual evidence triage"
  },
  {
    id: "mediation-expert",
    name: "Settlement Prep Agent",
    displayName: "Settlement Prep Specialist",
    description:
      "Builds mediation prep briefs, negotiation framing, and settlement talking points.",
    modelId: "gpt-5.4-mini",
    specialty: "Negotiation planning"
  },
  {
    id: "fraud-risk",
    name: "Fraud Risk Screening Agent",
    displayName: "Fraud Risk Screening Sub Agent",
    description:
      "Flags inconsistencies and risk markers to support adjuster review.",
    modelId: "gpt-5.4-mini",
    specialty: "Anomaly review"
  },
  {
    id: "coverage-gap",
    name: "Claim Gap Review Agent",
    displayName: "Claim Gap Review Specialist",
    description:
      "Identifies documentation and evidence gaps that could impact payout.",
    modelId: "gpt-5.4-mini",
    specialty: "Documentation completeness"
  },
  {
    id: "intake-conversion",
    name: "Intake Conversion Agent",
    displayName: "Intake Conversion Specialist",
    description:
      "Turns a new matter, lead, or policy intake into a structured next-step plan.",
    modelId: "gpt-5.4-mini",
    specialty: "Intake triage"
  }
];

export const SUB_AGENT_CONFIGS: SubAgentConfig[] = BASE_SUB_AGENT_CONFIGS.map(
  (config) => ({
    ...config,
    enabled: SUB_AGENT_TOGGLES[config.id]
  })
);

export const ENABLED_SUB_AGENT_CONFIGS: SubAgentConfig[] =
  SUB_AGENT_CONFIGS.filter((agent) => agent.enabled);

export function getEnabledSubAgentIds(): SubAgentId[] {
  return ENABLED_SUB_AGENT_CONFIGS.map((agent) => agent.id);
}

export function getSubAgentDisplayNameById(
  agentId?: string | null
): string | undefined {
  if (typeof agentId !== "string" || agentId.trim().length === 0) {
    return undefined;
  }

  return SUB_AGENT_CONFIGS.find((agent) => agent.id === agentId)?.displayName;
}

export function getSubAgentConfig(
  agentId: SubAgentId,
  options: { allowDisabled?: boolean } = {}
): SubAgentConfig {
  const found = SUB_AGENT_CONFIGS.find((agent) => agent.id === agentId);
  if (!found) {
    throw new Error(`Unknown sub-agent: ${agentId}`);
  }
  if (!options.allowDisabled && !found.enabled) {
    throw new Error(`Sub-agent is disabled: ${agentId}`);
  }
  return found;
}
